import React from "react";
import { useState, useRef, useEffect} from "react";
import styled from "styled-components";

import {
  Box,
  Typography,
  Button,
  LinearProgress,
  IconButton,
  Stack,
  Paper,
} from '@mui/material';

import CancelIcon from '@mui/icons-material/Cancel';

import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Formatter from "../lib/Formatter";
import AppDelegate from "../AppDelegate";

const Container = styled.div``;



const UploadFileComp = (props) => {
  const { caseId, token } = props;
  const [files, setFiles] = useState([]);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef(null);


  useEffect(() => {
    let done = true;
    const fileArray = [];
    if (files.length > 0) {
      for (const f of files) {
        done = done && f.success;
        fileArray.push({
          filename: f.name,
          url: f.url,
        });
      }
      if (done) {
        setSuccess(done);
        AppDelegate.reqFinishUpload({
          caseId: caseId,
          token: token,
          files: fileArray,
        })
      }
    }
  }, [files, caseId, token]);


  const onSelectedFile = (selectedFiles) => {
    const fileArray = Array.from(selectedFiles).map((file) => ({
      file,
      name: file.name,
      type: file.type,
      size: file.size,
      progress: 0,
      uploading: false,
      success: false,
    }));

    setFiles((prev) => [...prev, ...fileArray]);
  };

  const onClickClearAll = (e) => {
    setFiles([]);
  };

  const onClickClear = (file) => {
    const newArray = [];
    for (const f of files) {
      if (f.name !== file.name) {
        newArray.push(f);
      }
    }
    setFiles(newArray);
  };

  const onClickUploadFile = async (e) => {
    e.preventDefault();
    setUploadingCount(files.length);

    // set all files to be uploading
    const newfiles = [...files];
    for (const f of newfiles) {
      f.uploading = true;
    }
    setFiles(newfiles);

    // ask backend for GCS upload URL
    const request = {
      caseId: caseId,
      token: token,
      files: files.map((f) => ({
        filename: f.name,
        contentType: f.type,
      })),
    };
    const response = await AppDelegate.reqGenUploadURL(request);
    const payload = response.payload;

    // upload the file to GCS
    for (const file of files) {
      const xhr = new XMLHttpRequest();

      xhr.open('PUT', payload.urls[file.name].url, true);
      xhr.setRequestHeader('Content-Type', file.type);

      xhr.onprogress = (e) => {
        if (e.lengthComputable) {
          const percent = (e.loaded / e.total) * 100;
          setFiles(prev =>
            prev.map(f =>
              f.name === file.name ? { ...f, progress: percent } : f
            )
          );
        }
      };

      xhr.onload = () => {
        if (xhr.status === 200 || xhr.status === 201) {
          const json = JSON.parse(xhr.responseText);
          setFiles(prev =>
            prev.map(f =>
              f.name === file.name ? { ...f, uploading: false, success: true, url: json.mediaLink} : f
            )
          );
        } else {
          setSuccess(false);
          console.error('Upload failed with status ' + xhr.status);
          alert('Upload failed with status ' + xhr.status);
        }
      };
      xhr.onloadend = (e) => {
      }
      xhr.onerror = (e) => {
        setSuccess(false);
        alert('Upload error '+ xhr.status);
      }
      xhr.send(file.file);
    }
  };



  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files.length > 0) {
      const newArray = [...files, ...e.dataTransfer.files];
      onSelectedFile(newArray);
    }
  };

  return (
    <Container>
      <Box
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        sx={{
          backgroundColor: '#fff',
          p: 4,
          borderRadius: 2,
          maxWidth: 700,
          mx: 'auto',
          mt: 6,
          boxShadow: 3,
          textAlign: 'center',
        }}
      >
        <CloudUploadIcon sx={{ fontSize: 60, color: '#777777', mb: 1 }} />
        <Typography variant="h5" sx={{ color: '#1a365d', mb: 1 }}>
          Upload Mobile Data
        </Typography>
        <Typography color="textSecondary" sx={{ mb: 3 }}>
          Select a file to begin extraction. Only zip file is supported.
        </Typography>


        {(!success && uploadingCount === 0) && (<Box
          sx={{
            border: '2px dashed #e0e0e0',
            borderRadius: 2,
            py: 5,
            px: 2,
            mb: 3,
            cursor: 'pointer',
            '&:hover': { backgroundColor: '#f9f9f9' },
          }}
          onClick={() => fileInputRef.current?.click()}
        >
          <Typography fontWeight="bold" color="primary">
            Drag & Drop or Click to Browse
          </Typography>
          <Typography variant="body2" color="textSecondary">
            File type: zip
          </Typography>
          <input
            type="file"
            multiple
            ref={fileInputRef}
            onChange={(e) => onSelectedFile(e.target.files)}
            accept=".zip,.txt,.pdf"
            hidden
          />
        </Box>)}

        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          marginBottom: 1,
        }}>
          <Typography fontWeight={400} fontSize={18} >Files to upload: {files.length}</Typography>
          <Button variant="outlined" onClick={onClickClearAll} disabled={uploadingCount>0}>
            Clear All
          </Button>
        </Box>
        {files.map((file) => {
          return <Paper key={'paper_'+file.name} sx={{ p: 2, mb: 2, backgroundColor: '#f5f5f5' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box sx={{
                textAlign: "left",
              }}>
                <Typography fontWeight={600}>
                  {file.name}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {Formatter.formatFileSize(file.size)}
                </Typography>
              </Box>
              <IconButton onClick={() => {onClickClear(file)}} color="error" disabled={success}>
                <CancelIcon />
              </IconButton>
            </Stack>

            {(file.uploading || file.progress === 100) && (<Box>
                <LinearProgress
                  key={'progress_'+file.name}
                  variant="determinate"
                  value={file.progress}
                  sx={{ mt: 2, borderRadius: 1 }}
                />
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ mt: 1, textAlign: 'right' }}
                >
                  {Math.round(file.progress)}%
                </Typography>
              </Box>)
            }
          </Paper>
        })}

        {success && (
          <Typography sx={{ color: '#28a745', fontWeight: 600, mt: 2 }}>
            ✅ File uploaded successfully! We will contact you soon.<br/>
            You can close this window now.
          </Typography>
        )}

        {(files.length>0 && !success) && (
          <Stack direction="row" spacing={2} justifyContent="center" mt={3}>
            <Button variant="contained" disabled={(success)} color="warning" onClick={onClickUploadFile}>
              Upload File
            </Button>
          </Stack>
        )}

      </Box>
    </Container>
  );
};

export default UploadFileComp;




